import { toast } from 'sonner'
import type { ReactNode } from 'react'

/**
 * Deduplicated error toasts.
 *
 * Two things collapse duplicate error notifications:
 *  1. A stable sonner `id` per error key, so identical errors arriving at the
 *     same time (e.g. several endpoints failing together) reuse one toast
 *     instead of stacking.
 *  2. A short time-window guard, so the same error re-firing within the window
 *     (e.g. a request being retried by react-query) is swallowed rather than
 *     shown again.
 *
 * A plain-string message is its own key. For JSX/ReactNode content there is no
 * derivable key, so callers must pass an explicit `key`.
 */

const DEFAULT_WINDOW_MS = 4000

// key -> timestamp (ms) of the last time we showed it
const recentErrors = new Map<string, number>()

function prune(now: number, windowMs: number): void {
  recentErrors.forEach((ts, key) => {
    if (now - ts >= windowMs) {
      recentErrors.delete(key)
    }
  })
}

interface ToastDedupOptions {
  /** Explicit dedup key. Required when `content` is not a plain string. */
  key?: string
  /** Suppression window in ms. Defaults to 4000. */
  windowMs?: number
}

export function toastErrorDeduped(
  content: string | (() => ReactNode) | ReactNode,
  options: ToastDedupOptions = {}
): void {
  const windowMs = options.windowMs ?? DEFAULT_WINDOW_MS
  const key = options.key ?? (typeof content === 'string' ? content : undefined)

  // Without a key we cannot dedup; fall back to a normal toast.
  if (!key) {
    toast.error(content as any)
    return
  }

  const now = Date.now()
  prune(now, windowMs)

  const last = recentErrors.get(key)
  if (last !== undefined && now - last < windowMs) {
    // Same error seen very recently — refresh the timestamp so a sustained
    // burst keeps extending the suppression window, and skip showing it again.
    recentErrors.set(key, now)
    return
  }

  recentErrors.set(key, now)
  // A stable `id` also collapses truly simultaneous duplicates within sonner.
  toast.error(content as any, { id: key })
}
